import React, { useContext, useMemo } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';

import MemoSelector, { MemorySelectorResult } from '../Rule/MemorySelector';
import { StyledRow } from 'src/presentation/components/pages/sidekick/threads/components/Layout';
import { FieldContained } from '@ad-scripts/presentation/components/common/input_field';
import { Row } from './Layout';
import { useCondition } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/Components/Rule/useCondition';
import FormContext from 'src/presentation/contexts/FormContext';

type TemplateInputWrapperProps = {
  'data-aid'?: string;
  attr: string;
  description?: string;
  disabled: boolean;
};

const TemplateInputWrapper = ({ 'data-aid': dataAid, attr, description, disabled }: TemplateInputWrapperProps) => {
  const { workingState } = useContext(FormContext);

  const templateAttr = `${attr.split('.').slice(0, -1).join('.')}.template`;
  const templateId = get(workingState, templateAttr) as string | undefined;

  const templateInputStr = useMemo(() => (get(workingState, attr) || []).join('.'), [attr, workingState]);

  if (!templateId) {
    return null;
  }

  return <TemplateInput key={templateInputStr} {...{ 'data-aid': dataAid, attr, description, disabled }} />;
};

type TemplateInputProps = {
  'data-aid'?: string;
  attr: string;
  description?: string;
  disabled: boolean;
};

const TemplateInput = ({ 'data-aid': dataAid, attr, description, disabled }: TemplateInputProps) => {
  const { workingState, onBlur } = useContext(FormContext);

  const fields = get(workingState, attr) || [];

  const { currentFields, setCurrentFields, optGroups } = useCondition({
    fields,
    allowEnterManually: false,
    compatibilityMatch: false,
    includeOnlyNestedProps: true,
  });

  const handleChangeMemoFields = props => {
    const currentProperties = props.currentState;
    const value = get(currentProperties, attr, []);
    const newProps = { currentState: set(currentProperties, attr, value) };
    onBlur && onBlur(newProps);
  };

  return (
    <Row>
      <FieldContained
        attr={attr}
        data-aid={dataAid}
        description={description}
        control={
          <StyledRow>
            <MemoSelector
              attr={attr}
              disabled={disabled}
              isEnterManually={false}
              optGroups={optGroups}
              fields={currentFields}
              setCurrentFields={setCurrentFields}
              onChange={handleChangeMemoFields}
              resultType={MemorySelectorResult.ARRAY}
            />
          </StyledRow>
        }
      />
    </Row>
  );
};

export default TemplateInputWrapper;
